import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ShoppingCart, X } from 'lucide-react';

import { useCredits, ActionType } from '../../hooks/useCredits';
import { pickLowActionTypes, LOW_CREDIT_THRESHOLD } from '../../pages/topup-helpers';
import { Button } from '../ui';

import styles from './GlobalLowCreditBanner.module.css';

const ACTION_LABEL: Record<ActionType, string> = {
  simulation_official: 'simulados de provas anteriores',
  correction: 'correções',
  exam_creation: 'criações de simulado',
  student_correction: 'correções de aluno',
};

const DISMISS_KEY = 'low-credit-banner-dismissed';
const DISMISS_SIG_KEY = 'low-credit-banner-dismissed-sig';

const HIDDEN_PATHS: readonly string[] = ['/assinatura'];

/**
 * Fase 3 — Decide se o banner global de saldo baixo deve aparecer.
 *
 * Helper puro, testável sem DOM. Regras:
 * - rota atual não está na lista de rotas ocultas (`/assinatura` etc.);
 * - existe ao menos um actionType relevante com saldo <= threshold;
 * - usuário não dispensou (ou dispensou para uma combinação diferente de
 *   actionTypes, caso em que deve voltar a mostrar).
 */
export function shouldShowLowCreditBanner(input: {
  lowActionTypes: readonly string[];
  currentPath: string;
  dismissedSignature: string | null;
}): boolean {
  if (input.lowActionTypes.length === 0) return false;
  if (HIDDEN_PATHS.some((path) => input.currentPath.startsWith(path))) return false;
  const signature = computeSignature(input.lowActionTypes);
  if (input.dismissedSignature && input.dismissedSignature === signature) return false;
  return true;
}

/**
 * Assinatura estável do conjunto de actionTypes baixos — usada para
 * invalidar o dismiss quando o conjunto muda (ex.: usuário dispensou com
 * `simulation_official` baixo; depois correction também ficou baixo → novo
 * conjunto → banner reaparece).
 */
export function computeSignature(lowActionTypes: readonly string[]): string {
  return [...lowActionTypes].sort().join('|');
}

export function buildBannerMessage(lowActionTypes: readonly ActionType[]): string {
  if (lowActionTypes.length === 0) return '';
  const labels = lowActionTypes.map((at) => ACTION_LABEL[at] ?? at);
  if (labels.length === 1) return `Você está com saldo baixo de ${labels[0]}.`;
  return `Você está com saldo baixo de: ${labels.join(', ')}.`;
}

function readDismissedSignature(): string | null {
  try {
    if (sessionStorage.getItem(DISMISS_KEY) !== 'true') return null;
    return sessionStorage.getItem(DISMISS_SIG_KEY);
  } catch {
    return null;
  }
}

function writeDismissed(signature: string): void {
  try {
    sessionStorage.setItem(DISMISS_KEY, 'true');
    sessionStorage.setItem(DISMISS_SIG_KEY, signature);
  } catch {
    // noop — sessionStorage indisponível (ex.: SSR/incógnito estrito)
  }
}

/**
 * Fase 3 — Banner global de saldo baixo exibido em todas as rotas
 * autenticadas exceto `/assinatura`. Dismissível por sessão.
 */
interface Props {
  currentPath: string;
}

export default function GlobalLowCreditBanner({ currentPath }: Props) {
  const navigate = useNavigate();
  const { balance } = useCredits();
  const [dismissedSignature, setDismissedSignature] = useState<string | null>(() =>
    readDismissedSignature()
  );

  const lowActionTypes = useMemo(
    // `correction` (correção com transcrição) é omitido do alerta global de
    // saldo baixo — esse saldo é comunicado apenas dentro da área de Correções.
    () =>
      pickLowActionTypes<ActionType>(balance, LOW_CREDIT_THRESHOLD).filter(
        (at) => at !== 'correction'
      ),
    [balance]
  );

  useEffect(() => {
    // Se o conjunto de actionTypes baixos mudou, atualiza dismissedSignature
    // lendo do storage (outra aba pode ter alterado).
    setDismissedSignature(readDismissedSignature());
  }, [currentPath]);

  const show = shouldShowLowCreditBanner({
    lowActionTypes,
    currentPath,
    dismissedSignature,
  });

  if (!show) return null;

  const message = buildBannerMessage(lowActionTypes);
  const signature = computeSignature(lowActionTypes);

  const handleDismiss = () => {
    writeDismissed(signature);
    setDismissedSignature(signature);
  };

  const handleCta = () => {
    navigate('/assinatura#topups');
  };

  return (
    <div role="status" className={styles.banner}>
      <div className={styles.content}>
        <AlertCircle size={18} className={styles.icon} aria-hidden="true" />
        <span className={styles.message}>{message}</span>
      </div>
      <div className={styles.actions}>
        <Button variant="primary" size="sm" onClick={handleCta}>
          <ShoppingCart size={14} aria-hidden />
          Comprar pacote avulso
        </Button>
        <button
          type="button"
          className={styles.dismissBtn}
          onClick={handleDismiss}
          aria-label="Dispensar aviso de saldo baixo"
        >
          <X size={16} aria-hidden />
        </button>
      </div>
    </div>
  );
}
